"use client";

import { useState, useEffect, useCallback } from 'react';
import { dmsApi } from '@/lib/api';
import { ShieldCheck, Loader2 } from 'lucide-react';
import { toast } from '@/hooks/use-toast';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';

interface RoleRow {
  _id: string;
  name: string;
  permissions?: string[];
}

export function RolesPage() {
  const [roles, setRoles] = useState<RoleRow[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  const loadRoles = useCallback(async () => {
    setIsLoading(true);
    try {
      const data = await dmsApi.roles.list();
      setRoles(Array.isArray(data) ? data : []);
    } catch (err: any) {
      toast({
        variant: 'destructive',
        title: 'Error',
        description: err?.error ?? err?.message ?? 'Failed to load roles.',
      });
      setRoles([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadRoles();
  }, [loadRoles]);

  return (
    <div className="flex flex-col h-full overflow-auto">
      <Card className="m-6 flex-1 min-h-0 flex flex-col">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <ShieldCheck size={24} />
            Roles
          </CardTitle>
          <CardDescription>
            All roles and their assigned permissions.
          </CardDescription>
        </CardHeader>
        <CardContent className="flex-1 overflow-auto">
          {isLoading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          ) : roles.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">No roles found.</p>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[200px]">Role name</TableHead>
                  <TableHead>Permissions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {roles.map((role) => (
                  <TableRow key={role._id}>
                    <TableCell className="font-medium align-top">{role.name ?? '—'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1.5">
                        {Array.isArray(role.permissions) && role.permissions.length > 0 ? (
                          role.permissions.map((p) => (
                            <Badge key={p} variant="secondary" className="text-xs font-normal">
                              {p}
                            </Badge>
                          ))
                        ) : (
                          <span className="text-muted-foreground text-sm">—</span>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
